/** Send the original bytes, without FileReader/base64/JSON copies. */
export interface ImageUploadResult { ok: true; readCommand: string; paths: string[]; count: number }
export function uploadImages(files: File[], options: { signal?: AbortSignal; onProgress?: (percent: number) => void } = {}): Promise<ImageUploadResult> {
  if (!files.length || files.length > 20) return Promise.reject(new Error('Bitte 1 bis 20 Bilder auswählen.'));
  if (files.some(f => !f.type.startsWith('image/'))) return Promise.reject(new Error('Bitte nur Bilddateien einfügen.'));
  if (files.some(f => f.size > 25 * 1024 * 1024) || files.reduce((n, f) => n + f.size, 0) > 50 * 1024 * 1024) {
    return Promise.reject(new Error('Maximal 25 MB je Bild und 50 MB insgesamt.'));
  }
  if (options.signal?.aborted) return Promise.reject(new DOMException('Abgebrochen', 'AbortError'));
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    const finish = (error?: Error, result?: ImageUploadResult) => {
      options.signal?.removeEventListener('abort', abort);
      if (error) reject(error); else resolve(result!);
    };
    xhr.open('POST', '/api/images/file');
    xhr.timeout = 90000;
    xhr.upload.onprogress = e => { if (e.lengthComputable) options.onProgress?.(Math.min(100, Math.round(e.loaded / e.total * 100))); };
    xhr.onerror = () => finish(new Error('Verbindung unterbrochen. Du kannst den Upload erneut versuchen.'));
    xhr.ontimeout = () => finish(new Error('Der Upload dauert zu lange. Bitte erneut versuchen.'));
    xhr.onabort = () => finish(new DOMException('Abgebrochen', 'AbortError'));
    xhr.onload = () => {
      let data: any;
      try { data = JSON.parse(xhr.responseText); } catch { finish(new Error('Der Server hat keine gültige Upload-Antwort geliefert. Bitte erneut versuchen.')); return; }
      if (xhr.status < 200 || xhr.status >= 300) { finish(new Error(data.error || `Upload fehlgeschlagen (HTTP ${xhr.status}).`)); return; }
      if (data.ok !== true || typeof data.readCommand !== 'string' || !data.readCommand.trim() || !Array.isArray(data.paths) || data.paths.length !== files.length || data.paths.some((p: unknown) => typeof p !== 'string' || !p)) {
        finish(new Error('Der Upload wurde nicht vollständig bestätigt. Bitte erneut versuchen.')); return;
      }
      finish(undefined, data);
    };
    options.signal?.addEventListener('abort', abort, { once: true });
    const body = new FormData();
    files.forEach(file => body.append('images', file, file.name));
    xhr.send(body);
  });
}
